import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Typography,
  Select,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { useFormContext } from "../../hooks/useFormContext";
import { getProductApi } from "../../services/Product/ProductApis";
import WizardStepLayout from "./WizardStepLayout";
interface StepProps {
  loading: boolean;
  gotoStep: Function;
  current: number;
  submit: Function;
  form: any;
  /** Absolute step index for the error Result screen (defaults to current + 2). */
  failStep?: number;
  /** Primary action label. Use "Next" when more wizard steps follow. */
  actionLabel?: string;
}

const resolveProductId = (product: any): string | number | null => {
  if (product == null || product === "") return null;
  if (typeof product === "object") return product.value ?? product.id ?? null;
  return product;
};

const dedupeAddons = (addons: any[]): any[] => {
  const seen = new Set<number>();
  const result: any[] = [];
  for (const addon of addons || []) {
    const id = Number(addon?.id);
    if (!id || Number.isNaN(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(addon);
  }
  return result;
};

const Addon: React.FC<StepProps> = ({
  loading,
  gotoStep,
  current,
  submit,
  form,
  failStep,
  actionLabel = "Submit",
}) => {
  const { productInfo, coveredProductInfo } = useFormContext();
  const [linkedAddons, setLinkedAddons] = useState<any[]>([]);
  const [selectedAddonId, setSelectedAddonId] = useState<number | null>(null);

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  useEffect(() => {
    let cancelled = false;

    const loadAddons = async () => {
      const formProductId = resolveProductId(form?.getFieldValue?.("Product"));
      const productId = productInfo?.id ?? formProductId;
      const coveredId =
        typeof coveredProductInfo === "number"
          ? coveredProductInfo
          : coveredProductInfo?.id ?? null;

      const collected: any[] = [];

      if (Array.isArray(productInfo?.Addon) && productInfo.Addon.length > 0) {
        collected.push(...productInfo.Addon);
      } else if (productId) {
        try {
          const { data } = await getProductApi(String(productId));
          if (Array.isArray(data?.Addon)) {
            collected.push(...data.Addon);
          }
        } catch (error) {
          console.error("Error loading product addons:", error);
        }
      }

      if (coveredId) {
        if (
          Array.isArray(coveredProductInfo?.Addon) &&
          coveredProductInfo.Addon.length > 0
        ) {
          collected.push(...coveredProductInfo.Addon);
        } else {
          try {
            const { data } = await getProductApi(String(coveredId));
            if (Array.isArray(data?.Addon)) {
              collected.push(...data.Addon);
            }
          } catch (error) {
            console.error("Error loading covered product addons:", error);
          }
        }
      }

      if (!cancelled) {
        setLinkedAddons(dedupeAddons(collected));
      }
    };

    loadAddons();

    return () => {
      cancelled = true;
    };
  }, [productInfo, coveredProductInfo, form]);

  // Drop qty <= 0 leftovers from the old UI; enrich Name/Premium from catalog
  useEffect(() => {
    if (!form?.getFieldValue) return;

    const current = form.getFieldValue("Addons") || [];
    if (!Array.isArray(current) || current.length === 0) return;

    const byId = new Map(
      linkedAddons.map((addon) => [Number(addon.id), addon] as const)
    );

    let changed = false;
    const next = current
      .filter((row: any) => {
        const qty = Number(row?.quantity ?? row?.Quantity ?? 0);
        if (qty > 0) return true;
        changed = true;
        return false;
      })
      .map((row: any) => {
        const id = Number(row?.id);
        const catalog = byId.get(id);
        if (!catalog) return row;
        const Name = row?.Name || catalog.Name;
        const Premium =
          row?.Premium != null && row?.Premium !== ""
            ? row.Premium
            : catalog.Premium ?? catalog.PolicyPremium ?? null;
        if (Name === row?.Name && Premium === row?.Premium) return row;
        changed = true;
        return { ...row, Name, Premium };
      });

    if (changed) {
      form.setFieldsValue({ Addons: next });
    }
  }, [linkedAddons, form]);

  const content = (
    <WizardStepLayout
      action={
        <Form.Item {...tailLayout} style={{ marginBottom: 0 }}>
          <Button
            loading={loading}
            block
            size="large"
            onClick={() => {
              submit().then((result: any) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(failStep ?? current + 2);
                }
              });
            }}
          >
            {actionLabel}
          </Button>
        </Form.Item>
      }
    >
      <div style={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      <Form.List name="Addons">
        {(fields, { add, remove }) => {
          const selectedIds = new Set(
            (form.getFieldValue("Addons") || [])
              .map((row: any) => Number(row?.id))
              .filter((id: number) => id && !Number.isNaN(id))
          );

          const options = linkedAddons
            .filter((addon) => !selectedIds.has(Number(addon.id)))
            .map((addon) => ({
              value: Number(addon.id),
              label:
                addon.Premium != null && addon.Premium !== ""
                  ? `${addon.Name || `Addon #${addon.id}`} — R${Number(
                      addon.Premium
                    ).toFixed(2)}`
                  : addon.Name || `Addon #${addon.id}`,
            }));

          const handleAdd = () => {
            if (selectedAddonId == null) {
              message.warning("Select an addon to add.");
              return;
            }
            if (selectedIds.has(selectedAddonId)) {
              message.warning("That addon is already on the list.");
              return;
            }
            const catalog = linkedAddons.find(
              (addon) => Number(addon.id) === selectedAddonId
            );
            if (!catalog) {
              message.error("Addon not found for this product.");
              return;
            }
            add({
              id: Number(catalog.id),
              Name: catalog.Name,
              Premium: catalog.Premium ?? catalog.PolicyPremium ?? null,
              quantity: 1,
            });
            setSelectedAddonId(null);
          };

          return (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 16,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={
                    linkedAddons.length === 0
                      ? "No addons linked to this product"
                      : "Select an addon"
                  }
                  disabled={linkedAddons.length === 0}
                  style={{ flex: 1, minWidth: 0 }}
                  value={selectedAddonId ?? undefined}
                  options={options}
                  onChange={(value) =>
                    setSelectedAddonId(
                      typeof value === "number" ? value : null
                    )
                  }
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={
                    linkedAddons.length === 0 || selectedAddonId == null
                  }
                  onClick={handleAdd}
                >
                  Add
                </Button>
              </div>

              {fields.length === 0 ? (
                <Typography.Text type="secondary">
                  {linkedAddons.length === 0
                    ? "No addons linked to this product."
                    : "No addons selected. Choose one above and click Add."}
                </Typography.Text>
              ) : (
                fields.map((field) => (
                  <div
                    key={field.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      marginBottom: 12,
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    <Form.Item name={[field.name, "id"]} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name={[field.name, "Name"]} hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item name={[field.name, "Premium"]} hidden>
                      <Input />
                    </Form.Item>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Form.Item
                        shouldUpdate={(prev, next) =>
                          prev?.Addons?.[field.name]?.Name !==
                            next?.Addons?.[field.name]?.Name ||
                          prev?.Addons?.[field.name]?.Premium !==
                            next?.Addons?.[field.name]?.Premium
                        }
                        noStyle
                      >
                        {() => {
                          const row =
                            form.getFieldValue(["Addons", field.name]) || {};
                          return (
                            <>
                              <Typography.Text strong>
                                {row.Name || `Addon #${row.id}`}
                              </Typography.Text>
                              {row.Premium != null && row.Premium !== "" ? (
                                <Typography.Text
                                  type="secondary"
                                  style={{ display: "block" }}
                                >
                                  Premium: R
                                  {Number(row.Premium).toFixed(2)} each
                                </Typography.Text>
                              ) : null}
                            </>
                          );
                        }}
                      </Form.Item>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <Typography.Text style={{ whiteSpace: "nowrap" }}>
                        Qty
                      </Typography.Text>
                      <Form.Item
                        name={[field.name, "quantity"]}
                        noStyle
                        rules={[
                          {
                            required: true,
                            message: "Qty required",
                          },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: 80 }}
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Remove addon"
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  </div>
                ))
              )}
            </>
          );
        }}
      </Form.List>
    </div>
    </WizardStepLayout>
  );

  return content;
};
export default Addon;
